import { ChangeEvent, FocusEvent, FormEvent, ReactNode } from 'react';
import { VALIDITY } from '@/constants/validity';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Input } from '@/ui-components/atoms/Inputs/Input/Input';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import {
    AppDownloadButtons,
    DownloadButtonProps,
} from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import styles from './snapshotpopover.module.scss';
import SXMLink from '@/src/shared-components/SXMLink';
import { buildCareRouteUrl } from './careAccountUrls';

const REGISTER_URL = buildCareRouteUrl('accountRegistration');
const FORGOT_LOGIN_URL = buildCareRouteUrl('accountCredentials');

interface SnapshotUserAnonymousSignInProps {
    appdownloadItems: DownloadButtonProps[];
    headingId: string;
    hasFieldError: boolean;
    identifier: string;
    invalidMessage?: ReactNode;
    isSubmitting?: boolean;
    onIdentifierBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onIdentifierChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
    showErrorSummary: boolean;
}

export function SnapshotUserAnonymousSignIn({
    appdownloadItems,
    headingId,
    hasFieldError,
    identifier,
    invalidMessage,
    isSubmitting = false,
    onIdentifierBlur,
    onIdentifierChange,
    onSubmit,
    showErrorSummary,
}: SnapshotUserAnonymousSignInProps): React.JSX.Element {
    return (
        <form className={styles.snapshotUserForm} onSubmit={onSubmit} noValidate>
            <div className={styles.snapshotAnonymousMain}>
                <div className={styles.snapshotAnonymousSection}>
                    <Text tag="h3" className={styles.snapshotAnonymousTitle} id={headingId}>
                        Sign in
                    </Text>

                    {showErrorSummary && (
                        <div className={styles.snapshotErrorSummary} role="alert" aria-live="assertive">
                            <Text tag="p" className={styles.snapshotErrorSummaryText}>
                                Please fix the error in the form below.
                            </Text>
                        </div>
                    )}

                    <Input
                        ariaLabel="Email or username"
                        inputWrapperStyles={styles.snapshotInputWrapper}
                        invalid={hasFieldError ? VALIDITY.invalid : VALIDITY.untouched}
                        invalidMessage={invalidMessage}
                        label="Email or username"
                        name="snapshot-identifier"
                        noSpacing={true}
                        onBlur={onIdentifierBlur}
                        onChange={onIdentifierChange}
                        required={true}
                        type="text"
                        value={identifier}
                    />
                </div>

                <div className={styles.snapshotAnonymousActions}>
                    <div className={styles.snapshotAnonymousButtonGroup}>
                        <div className={styles.snapshotAnonymousPrimaryActions}>
                            <Button
                                ariaLabel="Continue"
                                disabled={isSubmitting}
                                fitting="fill-container"
                                size="small"
                                type="submit"
                                variant="primary"
                            >
                                {isSubmitting ? 'Continuing...' : 'Continue'}
                            </Button>
                        </div>

                        <SXMLink className={styles.inlineActionButton} href={FORGOT_LOGIN_URL}>
                            Forgot your login?
                        </SXMLink>
                    </div>

                    <Text tag="p" className={styles.snapshotCopy}>
                        No account?{' '}
                        <Link className={styles.inlineLink} href={REGISTER_URL}>
                            Register
                        </Link>
                    </Text>
                </div>
            </div>

            <div className={styles.snapshotDivider}>
                <div className={styles.snapshotDividerLine} />
            </div>

            <div className={styles.snapshotAnonymousFooter}>
                <Text tag="h4" className={styles.snapshotAppHeading}>
                    Get the SiriusXM app
                </Text>
                <AppDownloadButtons
                    items={appdownloadItems}
                    className={styles.snapshotDownloadButtons}
                    appleClassName={styles.snapshotStoreBadge}
                    androidClassName={styles.snapshotStoreBadge}
                />
            </div>
        </form>
    );
}
